import { type FormEvent, useRef, useState } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/lib/auth-store";
import { useTilt } from "@/lib/use-tilt";
import { type SupportedLang } from "@/i18n/i18n";

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const status = useAuthStore((s) => s.status);
  const login = useAuthStore((s) => s.login);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tiltRef = useRef<HTMLDivElement>(null);
  useTilt(tiltRef);

  const currentLang = (i18n.language as SupportedLang) || "es";

  if (status === "authenticated") {
    const next = params.get("next") || "/shifts/my";
    return <Navigate to={next} replace />;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      const next = params.get("next") || "/shifts/my";
      navigate(next, { replace: true });
    } catch (err: unknown) {
      const r = (err as { response?: { status?: number } }).response;
      if (r?.status === 401) setError(t("auth.login.invalid"));
      else setError(t("auth.login.error_generic"));
    } finally {
      setSubmitting(false);
    }
  };

  const brandTitle = currentLang === "es" ? "Confianza en el Tiempo" : "Trust in Time";
  const tagline =
    currentLang === "es"
      ? "Gestión inteligente de turnos para equipos modernos."
      : "Smart shift management for modern teams.";

  return (
    <div className="login-page">
      <div className="login-card card">
        <div className="login-visual js-tilt" ref={tiltRef}>
          <img
            src="/static/img/favicon_250.png"
            alt="Trust in Time"
            className="login-logo"
          />
          <h1 className="login-brand">{brandTitle}</h1>
          <p className="login-tagline">{tagline}</p>
        </div>

        <form className="login-form" onSubmit={onSubmit} noValidate>
          {error && <p className="flash flash-error">{error}</p>}

          <div className="form-field">
            <label className="form-label" htmlFor="email">
              {t("auth.login.email")}
            </label>
            <input
              className="form-input"
              type="email"
              id="email"
              name="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
            />
          </div>

          <div className="form-field">
            <label className="form-label" htmlFor="password">
              {t("auth.login.password")}
            </label>
            <input
              className="form-input"
              type="password"
              id="password"
              name="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
            />
          </div>

          <div className="login-actions">
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? t("auth.login.signing_in") : t("auth.login.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
